// Auth Context for Green Door CRM
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, tenantAPI } from '../services/api';
import { cacheAuth, getCachedAuth } from '../services/offlineDB';

const AuthContext = createContext(null);

// Convert hex color to HSL string for CSS custom property (e.g. "132 46% 22%")
function hexToHslString(hex) {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return null;
  try {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  } catch {
    return null;
  }
}

const DEFAULT_PRIMARY = '132 46% 22%';

function applyBrandingToDOM(branding) {
  if (branding?.has_branding_control && branding?.primary_color) {
    const hsl = hexToHslString(branding.primary_color);
    if (hsl) {
      document.documentElement.style.setProperty('--primary', hsl);
    }
  } else {
    document.documentElement.style.setProperty('--primary', DEFAULT_PRIMARY);
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);
  const [isFarm, setIsFarm] = useState(false);
  const [isSyndicator, setIsSyndicator] = useState(false);

  const fetchTenantFlags = async (tenantId) => {
    if (!tenantId) return;
    try {
      const { default: api } = await import('../services/api');
      const res = await api.get(`/tenants/${tenantId}`);
      setIsFarm(res.data?.is_farm === true);
      setIsSyndicator(res.data?.is_syndicator === true);
    } catch { /* non-fatal */ }
  };

  const fetchAndApplyBranding = async () => {
    try {
      const res = await tenantAPI.getMyBranding();
      setTenantBranding(res.data);
      applyBrandingToDOM(res.data);
    } catch {
      // Non-fatal — keep default branding
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
          
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));

          // Warm SW cache for returning sessions
          warmServiceWorkerCache(token);

          // Apply tenant branding + fetch farm flag
          await fetchAndApplyBranding();
          await fetchTenantFlags(response.data?.tenant_id);
        } catch (error) {
          console.error('Auth verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Send the auth token to the service worker so it can pre-cache critical endpoints
  const warmServiceWorkerCache = (token) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
    const baseUrl = window.location.origin || '';
    navigator.serviceWorker.controller.postMessage({
      type: 'WARM_CACHE',
      token,
      baseUrl,
    });
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      // Cache auth for offline login
      cacheAuth(access_token, userData).catch(() => {});

      await fetchAndApplyBranding();
      await fetchTenantFlags(userData?.tenant_id);
      warmServiceWorkerCache(access_token);
      return userData;
    } catch (err) {
      // Network error — try cached auth (offline login bypass)
      const isNetworkError = !err.response;
      if (isNetworkError) {
        // Never restore cached auth over an active impersonation session.
        // If the system owner's stale token is in the service-worker cache and the
        // device reconnects during impersonation, the cached token would overwrite the
        // scoped impersonation JWT and drop tenant_id → "No data found" on reports.
        const isImpersonating = localStorage.getItem('impersonation_id');
        const currentToken    = localStorage.getItem('token');
        const currentUserStr  = localStorage.getItem('user');
        if (isImpersonating && currentToken && currentUserStr) {
          try {
            const userData = JSON.parse(currentUserStr);
            setUser(userData);
            setIsAuthenticated(true);
            console.info('[Auth] Offline during impersonation — keeping impersonation token');
            return userData;
          } catch { /* fall through to normal cached restore */ }
        }

        const cached = await getCachedAuth();
        if (cached) {
          localStorage.setItem('token', cached.token);
          localStorage.setItem('user', JSON.stringify(cached.user));
          setUser(cached.user);
          setIsAuthenticated(true);
          // Show a subtle notice that we're in offline mode
          console.info('[Auth] Offline login — using cached credentials');
          return { ...cached.user, _offlineLogin: true };
        }
      }
      throw err;
    }
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    await fetchAndApplyBranding();
    warmServiceWorkerCache(access_token);
    
    return userData;
  };

  const ownerRegister = async (data) => {
    const response = await authAPI.ownerRegister(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    await fetchAndApplyBranding();
    warmServiceWorkerCache(access_token);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setTenantBranding(null);
    // Reset to default branding on logout
    document.documentElement.style.setProperty('--primary', DEFAULT_PRIMARY);
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const isSystemOwner = () => hasRole('system_owner');
  const isTenantAdmin = () => hasRole('tenant_system_admin') || isSystemOwner();
  const isOwner = () => hasRole('owner');
  const isViewOnly = () => hasRole('tenant_other');
  const isFarmManager = () => hasRole('tenant_farm_manager');
  const hasFinancialAccess = () => user?.has_financial_access === true || isSystemOwner() || isTenantAdmin();
  const canEdit = () => !isViewOnly() && !hasRole('tenant_card_runner') && !hasRole('tenant_sale_clerk');
  const canEditCRM = () => canEdit() && !hasRole('tenant_team_member');

  // Tenant logo: only shown when branding control is on and a logo is set
  const tenantLogo = tenantBranding?.has_branding_control && tenantBranding?.logo_url
    ? tenantBranding.logo_url
    : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        register,
        ownerRegister,
        logout,
        hasRole,
        isSystemOwner,
        isTenantAdmin,
        isOwner,
        isViewOnly,
        isFarmManager,
        hasFinancialAccess,
        canEdit,
        canEditCRM,
        tenantBranding,
        tenantLogo,
        isFarm,
        isSyndicator,
        // User's preferred IANA timezone — use for all datetime display via fmtDatetime()
        // Defaults to Eastern Time (America/New_York) when not explicitly set in user profile
        userTimezone: user?.timezone || 'America/New_York',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
